import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, streamerService } from '../services/api';
import type { StreamerAdminDto, StreamerAccountSearchDto } from '../types';
import '../styles/AdminNews.css';

interface StreamerForm {
    accountDbId: number | null;
    username: string;
    isStreamer: boolean;
    forceLive: boolean;
    streamerName: string;
    liveLink1: string;
    liveLink2: string;
}

const emptyForm: StreamerForm = {
    accountDbId: null,
    username: '',
    isStreamer: true,
    forceLive: false,
    streamerName: '',
    liveLink1: '',
    liveLink2: '',
};

const AdminStreamers: React.FC = () => {
    const navigate = useNavigate();
    const [list, setList] = useState<StreamerAdminDto[]>([]);
    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState<StreamerAccountSearchDto[]>([]);
    const [form, setForm] = useState<StreamerForm>(emptyForm);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        const checkAndLoad = async () => {
            if (!authService.isAuthenticated()) {
                navigate('/login');
                return;
            }
            if (!(await authService.checkIsAdmin())) {
                navigate('/');
                return;
            }
            await loadList();
        };
        checkAndLoad();
    }, [navigate]);

    const loadList = async () => {
        try {
            setLoading(true);
            const data = await streamerService.getStreamersAdmin();
            setList(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Não foi possível carregar os streamers.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setError(null);
        setSearching(true);
        try {
            const term = search.trim();
            setSearchResults(await streamerService.searchAccounts(term));
        } catch (err) {
            setError('Erro ao buscar contas. Tente novamente.');
        } finally {
            setSearching(false);
        }
    };

    const handleSelectAccount = (account: StreamerAccountSearchDto) => {
        setMessage(null);
        setForm({
            accountDbId: account.accountDBID,
            username: account.username,
            isStreamer: account.isStreamer === 1,
            forceLive: false,
            streamerName: '',
            liveLink1: '',
            liveLink2: '',
        });
        setSearchResults([]);
        setSearch('');
    };

    const handleSelectExisting = (item: StreamerAdminDto) => {
        setMessage(null);
        setForm({
            accountDbId: item.accountDBID,
            username: item.username,
            isStreamer: item.isStreamer,
            forceLive: item.forceLive,
            streamerName: item.streamerName ?? '',
            liveLink1: item.liveLink1 ?? '',
            liveLink2: item.liveLink2 ?? '',
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const target = e.target;
        const name = target.name;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (form.accountDbId == null) {
            setError('Selecione uma conta primeiro (busque pelo nome).');
            return;
        }
        setError(null);
        setMessage(null);
        setSaving(true);
        try {
            await streamerService.updateStreamer(form.accountDbId, {
                isStreamer: form.isStreamer,
                forceLive: form.forceLive,
                streamerName: form.streamerName.trim() || null,
                liveLink1: form.liveLink1.trim() || null,
                liveLink2: form.liveLink2.trim() || null,
            });
            setMessage(`Streamer "${form.username}" salvo com sucesso.`);
            await loadList();
        } catch (err) {
            setError('Erro ao salvar. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="admin-page admin-panel-content">
            <h1 className="admin-page-title">Streamers / Lives</h1>
            <p className="admin-page-subtitle">
                Marque contas como streamer e defina os links de transmissão (até 2). Quando uma conta
                tiver &quot;Streamer&quot; ativo com ao menos um link, ela aparece na página de Lives (/live).
            </p>

            {error && <div className="admin-error">{error}</div>}
            {message && <div className="admin-success">{message}</div>}

            <div className="admin-grid">
                <div className="admin-list">
                    <div className="admin-list-header">
                        <h2>Streamers ativos</h2>
                        <button type="button" className="btn btn-secondary" onClick={() => { setForm(emptyForm); setMessage(null); }}>
                            Adicionar
                        </button>
                    </div>

                    {loading ? (
                        <div className="admin-loading">Carregando...</div>
                    ) : (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Conta</th>
                                    <th>Lives</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((item) => (
                                    <tr
                                        key={item.accountDBID}
                                        className={form.accountDbId === item.accountDBID ? 'selected' : ''}
                                        onClick={() => handleSelectExisting(item)}
                                    >
                                        <td>{item.username}</td>
                                        <td>{[item.liveLink1, item.liveLink2].filter(Boolean).length} link(s)</td>
                                        <td>
                                            {item.forceLive ? (
                                                <span className="admin-live-on">● AO VIVO</span>
                                            ) : (
                                                <span className="admin-live-none">off</span>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-small btn-secondary"
                                                onClick={(e) => { e.stopPropagation(); handleSelectExisting(item); }}
                                            >
                                                Editar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {list.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan={4}>Nenhum streamer cadastrado ainda.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    <div className="admin-list-header" style={{ marginTop: 24 }}>
                        <h2>Buscar conta</h2>
                    </div>
                    <form className="admin-search-row" onSubmit={handleSearch}>
                        <input
                            type="text"
                            placeholder="Nome da conta (ex: knightof)"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button type="submit" className="btn btn-secondary" disabled={searching || !search.trim()}>
                            {searching ? 'Buscando...' : 'Buscar'}
                        </button>
                    </form>

                    {searchResults.length > 0 && (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Conta</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {searchResults.map((item) => (
                                    <tr key={item.accountDBID}>
                                        <td>{item.username}</td>
                                        <td>{item.isStreamer === 1 ? 'Streamer' : '—'}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-small btn-primary"
                                                onClick={() => handleSelectAccount(item)}
                                            >
                                                Selecionar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {searchResults.length === 0 && !searching && search && (
                        <div className="admin-loading" style={{ padding: '12px 0' }}>
                            Nenhuma conta encontrada para &quot;{search}&quot;.
                        </div>
                    )}
                </div>

                <div className="admin-form-wrapper">
                    <h2>{form.accountDbId != null ? `Streamer: ${form.username}` : 'Novo streamer'}</h2>
                    <form className="admin-form" onSubmit={handleSave}>
                        {form.accountDbId == null && (
                            <div className="form-row">
                                <label>Busque a conta pelo nome acima e clique em &quot;Selecionar&quot;.</label>
                            </div>
                        )}
                        <div className="form-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <input
                                id="isStreamer"
                                name="isStreamer"
                                type="checkbox"
                                checked={form.isStreamer}
                                onChange={handleChange}
                            />
                            <label htmlFor="isStreamer">Streamer ativo (aparecer na página de Lives)</label>
                        </div>
                        <div className="form-row">
                            <label htmlFor="streamerName">Apelido de exibição (aparece na página; vazio = nome de login)</label>
                            <input
                                id="streamerName"
                                name="streamerName"
                                type="text"
                                maxLength={64}
                                value={form.streamerName}
                                onChange={handleChange}
                                placeholder="Ex.: Zakter"
                            />
                        </div>
                        <div className="form-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <input
                                id="forceLive"
                                name="forceLive"
                                type="checkbox"
                                checked={form.forceLive}
                                onChange={handleChange}
                            />
                            <label htmlFor="forceLive">
                                AO VIVO agora (manual — use para plataformas sem detecção automática, ex.: TikTok)
                            </label>
                        </div>
                        <div className="form-row">
                            <label htmlFor="liveLink1">Link da live 1 (principal)</label>
                            <input
                                id="liveLink1"
                                name="liveLink1"
                                type="url"
                                value={form.liveLink1}
                                onChange={handleChange}
                                placeholder="https://twitch.tv/seuusuário"
                            />
                        </div>
                        <div className="form-row">
                            <label htmlFor="liveLink2">Link da live 2 (opcional)</label>
                            <input
                                id="liveLink2"
                                name="liveLink2"
                                type="url"
                                value={form.liveLink2}
                                onChange={handleChange}
                                placeholder="https://youtube.com/live/..."
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving || form.accountDbId == null}
                        >
                            {saving ? 'Salvando...' : 'Salvar streamer'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdminStreamers;